import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Chip,
  InputAdornment,
} from '@mui/material';
import { useState } from 'react';

import { ErrorAlert, InfoAlert, TextField } from '@/components';
import { getEmailListMessage, parseEmailList } from '@/utils';

interface MultiEmailInputProps {
  label: string;
  emails: string[];
  setEmails: React.Dispatch<React.SetStateAction<string[]>>;
  inputAdornment?: React.ReactNode;
  onDisplayNames?: (names: Record<string, string>) => void;
}

interface Feedback {
  message: string;
  isError: boolean;
}

export const MultiEmailInput = ({
  label,
  emails,
  setEmails,
  inputAdornment,
  onDisplayNames,
}: MultiEmailInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [focusedChipIndex, setFocusedChipIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const handleDelete = (index: number) => {
    setEmails(prev => prev.filter((_, i) => i !== index));
    setFeedback(null);
  };

  const addEmails = (value: string, { report = true } = {}) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    const result = parseEmailList(trimmed, emails);
    const message = getEmailListMessage(result);
    const hasAdded = result.valid.length > 0;

    if (hasAdded) {
      setEmails(prev => [...prev, ...result.valid]);

      if (Object.keys(result.names).length > 0) {
        onDisplayNames?.(result.names);
      }
    }

    setInputValue(hasAdded ? '' : trimmed);
    setFeedback(report && message ? { message, isError: !hasAdded } : null);
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== 'input') return;

    if (value.endsWith(',') || value.endsWith(';') || value.endsWith(' ')) {
      addEmails(value, { report: false });
    } else {
      setInputValue(value);
      setFeedback(null);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    addEmails(`${inputValue}${event.clipboardData.getData('text')}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        addEmails(inputValue);
        break;

      case 'Backspace':
      case 'Delete':
        if (!inputValue && emails.length > 0 && focusedChipIndex === null) {
          setEmails(prev => prev.slice(0, -1));
          setFeedback(null);
        } else if (focusedChipIndex !== null) {
          handleDelete(focusedChipIndex);
          setFocusedChipIndex(null);
        }
        break;

      default:
        setFocusedChipIndex(null);
        break;
    }
  };

  const placeholder =
    inputValue || emails.length ? '' : 'Emails, comma or semicolon separated';

  return (
    <Box sx={styles.root}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        disableClearable
        fullWidth
        value={emails}
        sx={styles.autocomplete}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        renderTags={value =>
          value.map((email, index) => (
            <Chip
              key={`${email}-${index}`}
              label={email}
              size="small"
              variant="outlined"
              deleteIcon={<Close />}
              tabIndex={0}
              sx={styles.chip}
              onFocus={() => setFocusedChipIndex(index)}
              onDelete={() => handleDelete(index)}
            />
          ))
        }
        renderInput={params => (
          <TextField
            value={inputValue}
            label={label}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onBlur={e => addEmails(e.currentTarget.value)}
            onPaste={handlePaste}
            {...params}
            slotProps={
              inputAdornment
                ? {
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end" sx={styles.adornment}>
                          {inputAdornment}
                        </InputAdornment>
                      ),
                    },
                  }
                : undefined
            }
          />
        )}
      />
      {feedback && (
        <Box sx={styles.feedback}>
          {feedback.isError ? (
            <ErrorAlert error={feedback.message} />
          ) : (
            <InfoAlert message={feedback.message} />
          )}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      py: 1,
      px: 1.5,
    },
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
      py: 0.5,
      px: 0,
    },
  },
  chip: {
    mr: 0.5,
    mb: 0.5,
    '& .MuiChip-deleteIcon': {
      color: 'text.main',
      '&:hover': {
        opacity: '0.8',
      },
    },
  },
  adornment: {
    ml: 'auto',
  },
  feedback: {
    mt: 1,
  },
};
